import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";

function Stars({ value, size = "h-4 w-4" }) {
  return (
    <span className="inline-flex" aria-label={`${value} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`${size} ${n <= Math.round(value) ? "text-gold fill-gold" : "text-slate-300"}`} />
      ))}
    </span>
  );
}

function ReviewForm({ slug, onDone }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post(`/courses/${slug}/reviews`, { rating, comment });
      toast.success(r.data.message);
      setComment("");
      onDone && onDone();
    } catch (err) {
      toast.error(apiErr(err));
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="bg-subtle border border-border rounded-lg p-5 space-y-3" data-testid="review-form">
      <p className="font-display text-lg font-semibold text-navy">Write a review</p>
      <div className="flex items-center gap-1" data-testid="review-rating">
        {[1, 2, 3, 4, 5].map((n) => (
          <button type="button" key={n} onClick={() => setRating(n)} data-testid={`review-star-${n}`} aria-label={`${n} star`}>
            <Star className={`h-7 w-7 ${n <= rating ? "text-gold fill-gold" : "text-slate-300"} transition-colors`} />
          </button>
        ))}
      </div>
      <textarea required rows={3} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="review-comment" placeholder="Share what you learned and how the course helped…" className="w-full rounded-md border border-border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gold" />
      <button type="submit" disabled={busy} data-testid="review-submit" className="h-10 px-5 rounded-md bg-navy text-white font-semibold text-sm hover:bg-navy-light transition-colors disabled:opacity-60">
        {busy ? "Submitting…" : "Submit review"}
      </button>
      <p className="text-xs text-slate-400">Reviews appear on the course after admin approval.</p>
    </form>
  );
}

export function CourseReviews({ slug, canReview }) {
  const [data, setData] = useState({ reviews: [], average: null, count: 0 });
  const load = () => api.get(`/courses/${slug}/reviews`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [slug]); // eslint-disable-line

  return (
    <div data-testid="course-reviews">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-2xl font-bold text-navy">Learner Reviews</h2>
        {data.count > 0 && (
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Stars value={data.average} /><span className="font-semibold text-navy">{data.average}</span><span className="text-slate-400">({data.count})</span>
          </div>
        )}
      </div>
      {canReview && <div className="mb-6"><ReviewForm slug={slug} onDone={load} /></div>}
      {data.reviews.length === 0 ? (
        <p className="text-sm text-slate-400">No reviews yet{canReview ? " — be the first to review this course." : "."}</p>
      ) : (
        <div className="space-y-4">
          {data.reviews.map((rv, i) => (
            <div key={i} className="border border-border rounded-lg p-5" data-testid={`review-${i}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="h-9 w-9 rounded-full bg-navy text-gold font-display font-bold inline-flex items-center justify-center">{(rv.user_name || "L").charAt(0)}</span>
                  <span className="font-semibold text-navy text-sm">{rv.user_name}</span>
                </div>
                <Stars value={rv.rating} />
              </div>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">{rv.comment}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
